
import api from './api';
import { toast } from '@/components/ui/sonner';

/**
 * Service specifically for handling database connection status and checks
 */
export const dbConnectionService = {
  /**
   * Attempts to verify the MySQL database connection
   */
  verifyDatabaseConnection: async (): Promise<{ connected: boolean; message?: string }> => {
    // In production, we don't want to show this error for failed connections on initial load
    if (import.meta.env.PROD) {
      try {
        // Make a direct request to the database check endpoint
        const response = await api.get('/test');
        
        if (response.data.dbStatus === 'connected') {
          return { connected: true };
        } else {
          return { 
            connected: false, 
            message: response.data.message || 'Database connection issue detected' 
          };
        }
      } catch (error: any) {
        console.error('Database connection verification failed:', error);
        
        // Provide a helpful message based on the error type
        let message = 'Could not verify database connection';
        
        if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
          message = 'Cannot connect to server. Please check your internet connection.';
        }
        
        return { connected: false, message };
      }
    } else {
      // Development mode with more verbose connection handling
      try {
        // Make a direct request to the database check endpoint
        const response = await api.get('/test');
        
        if (response.data.dbStatus === 'connected') {
          return { connected: true };
        } else {
          return { 
            connected: false, 
            message: response.data.message || 'Database connection issue detected' 
          };
        }
      } catch (error: any) {
        console.error('Database connection verification failed:', error);
        
        // Provide a helpful message based on the error type
        let message = 'Could not verify database connection';
        
        if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
          message = 'Cannot connect to MySQL server. Please check it is running.';
        }
        
        return { connected: false, message };
      }
    }
  },

  /**
   * Displays database connection guidelines to help users troubleshoot connection issues
   */
  showConnectionHelp: () => {
    // Only show detailed help in development mode
    if (import.meta.env.PROD) {
      toast.info('Connection Issues?', {
        description: 'Please contact system administrator if this problem persists.',
        duration: 8000,
      });
    } else {
      toast.info('Database Connection Help', {
        description: 'Make sure MySQL is running and check your .env credentials',
        duration: 8000,
        action: {
          label: "View Help",
          onClick: () => {
            alert(`
              MySQL Connection Troubleshooting:
              1. Check that MySQL service is running
              2. Verify credentials in server/.env are correct
              3. Make sure the database 'doctor_appointments' exists
              4. Test MySQL connection directly using a client tool
              5. Check MySQL port (default 3306) is not blocked
            `);
          },
        },
      });
    }
  },
  
  /**
   * Check if mock data should be used based on connection status
   * In production, this will always return false
   */
  shouldUseMockData: async (): Promise<boolean> => {
    // In production, we never use mock data
    if (import.meta.env.PROD) {
      return false;
    }
    
    // In development, check if explicitly enabled through env
    return import.meta.env.VITE_USE_MOCK_DATA === 'true';
  }
};

export default dbConnectionService;
